import { Delete, Edit } from '@mui/icons-material';
import {
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { Project } from 'api/models';
import { remove } from 'operations';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import {
  ToastSeverity,
  confirmDialogState,
  projectState,
  toastStateSelector,
} from 'state';
import { DialogTypes, ModelTypes, RoutePaths } from 'types/types';
import { getRoutePath } from 'utils';
import useHasAccess from 'hooks/useHasAccess';
import { Permission } from 'state/permissionState';
import { clearProjectData } from 'api';

export function ProjectDetailsCard({ project }: { project?: Project | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [{ selectedProject, projects }, setProjectState] =
    useRecoilState(projectState);
  const setToast = useSetRecoilState(toastStateSelector);
  const setDialogStore = useSetRecoilState(confirmDialogState);
  const canEditAndDelete = useHasAccess([Permission.WRITE_PROJECT]);

  const {
    id,
    shortName,
    description,
    longDescription,
    name,
    requiredPatientFields,
  } = project ?? {};

  const KeyValueItem = ({
    label,
    value,
  }: {
    label: string;
    value: string | number | boolean;
  }) => (
    <Typography>
      <Box fontWeight="bold" display="inline">
        {label}:{' '}
      </Box>
      {value}
    </Typography>
  );

  const onProjectDeleteConfirm = async () => {
    try {
      await remove({
        type: ModelTypes.project,
        ids: {
          id: selectedProject?.id,
        },
      });
      const newProjects = projects.filter(project => project.id !== id);
      setProjectState({
        projects: newProjects,
        selectedProject: undefined,
      });
      navigate(getRoutePath(RoutePaths.projectSelector));
      setToast({
        isOpen: true,
        message: t('general.success.delete'),
        severity: ToastSeverity.success,
      });
    } catch (e) {
      setToast({
        isOpen: true,
        message: t('projectCreation.errors.deletionFailed'),
        severity: ToastSeverity.error,
      });
    }
  };

  const onProjectDelete = async () => {
    setDialogStore({
      isOpen: true,
      dialogType: DialogTypes.confirmDelete,
      action: onProjectDeleteConfirm,
    });
  };

  const onDeleteProjectDataConfirm = async () => {
    try {
      await clearProjectData(id ?? '');
      setToast({
        isOpen: true,
        message: t('general.success.delete'),
        severity: ToastSeverity.success,
      });
    } catch (e) {
      setToast({
        isOpen: true,
        message: t('projectCreation.errors.errorWhileDeleting'),
        severity: ToastSeverity.error,
      });
    }
  };

  const onDeleteProjectData = () => {
    setDialogStore({
      isOpen: true,
      dialogType: DialogTypes.confirmDelete,
      action: onDeleteProjectDataConfirm,
    });
  };

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Box
          display={'flex'}
          justifyContent={'end'}
          position={'absolute'}
          right={'30px'}
        >
          {canEditAndDelete && (
            <Button color={'error'} onClick={onDeleteProjectData}>
              {t('projectCreation.deleteProjectData')}
            </Button>
          )}
          <IconButton
            size="small"
            disabled={!canEditAndDelete}
            onClick={() =>
              navigate(
                getRoutePath(RoutePaths.projectEditor, {
                  projectId: id,
                }),
              )
            }
          >
            <Tooltip title={t('general.edit')}>
              <Edit fontSize="small" />
            </Tooltip>
          </IconButton>

          <IconButton
            onClick={onProjectDelete}
            size="small"
            disabled={!canEditAndDelete}
          >
            <Tooltip title={t('general.delete')}>
              <Delete fontSize="small" />
            </Tooltip>
          </IconButton>
        </Box>
        <Typography variant="h5" fontWeight={'bold'}>{`${t(
          'general.name',
        )}: ${name}`}</Typography>
        <Box>
          <KeyValueItem
            label={t('projectCreation.shortName')}
            value={shortName ?? ''}
          />
          <KeyValueItem
            label={t('projectCreation.description')}
            value={description ?? ''}
          />
          {requiredPatientFields && (
            <KeyValueItem
              label={t('projectCreation.requiredPatientFields')}
              value={
                requiredPatientFields
                  .map(field =>
                    t(`projectCreation.availablePatientFields.${field}`),
                  )
                  .join(', ') ?? ''
              }
            />
          )}

          <div
            dangerouslySetInnerHTML={{
              __html: longDescription ?? '',
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
}
